/**
 * Common API response schemas
 */

import { z, ZodError } from 'zod';
import type {
    JsonObject,
    JsonArray,
    JsonValue,
    SambaLatestReleaseQuery,
    SambaLatestReleaseResponse,
    SambaPlatform,
    SambaArch,
    SambaTrack,
    SambaPackageType,
    SambaWindowsPackageRelease,
    SambaWindowsTrackRelease,
} from '../common.types';
import type { SafeParseResult } from '../zod-utils';

/**
 * Common request primitives
 */
export const NonEmptyStringSchema: z.ZodType<string> = z.string().min(1);
export const PositiveIntSchema: z.ZodType<number> = z.coerce.number().int().positive();

export const PhoneNumberSchema: z.ZodType<string> = z.preprocess(
    (value: unknown) => {
        const stringParseResult: SafeParseResult<string> = z.string().safeParse(value);
        if (!stringParseResult.success) {
            return value;
        }
        const digitsOnly: string = stringParseResult.data.replace(/\D/g, '');
        if (digitsOnly.length !== 10) {
            return stringParseResult.data;
        }
        return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    },
    z.string().regex(/^\(\d{3}\) \d{3}-\d{4}$/)
);

/**
 * Generic success message response
 */
export const MessageResponseSchema = z.object({
    message: z.string(),
});

/**
 * Void response schema for 204 No Content responses
 */
export const VoidResponseSchema = z.void();

/**
 * Health check response
 */
export const HealthResponseSchema = z.object({
    status: z.enum(['ok', 'unhealthy']),
    timestamp: z.string(), // ISO date string
    database: z.object({
        connected: z.boolean(),
    }),
});

/**
 * Samba module version response
 */
export const SambaVersionResponseSchema = z.object({
    version: z.string(),
});

/**
 * Samba update release query schema
 */
export const SambaPlatformSchema: z.ZodType<SambaPlatform> = z.union([
    z.literal('mac'),
    z.literal('win'),
]);

export const SambaArchSchema: z.ZodType<SambaArch> = z.union([
    z.literal('x64'),
    z.literal('arm64'),
]);

export const SambaTrackSchema: z.ZodType<SambaTrack> = z.union([
    z.literal('stable'),
    z.literal('latest'),
]);

export const SambaPackageTypeSchema: z.ZodType<SambaPackageType> = z.union([
    z.literal('portable'),
    z.literal('installer'),
]);

export const SambaLatestReleaseQuerySchema: z.ZodType<SambaLatestReleaseQuery> = z.object({
    platform: SambaPlatformSchema,
    arch: SambaArchSchema,
});

/**
 * Samba latest release response schema
 */
export const SambaWindowsPackageReleaseSchema: z.ZodType<SambaWindowsPackageRelease> = z.object({
    arch: SambaArchSchema,
    packageType: SambaPackageTypeSchema,
    downloadUrl: NonEmptyStringSchema,
    sha256: NonEmptyStringSchema,
});

export const SambaWindowsTrackReleaseSchema: z.ZodType<SambaWindowsTrackRelease> = z.object({
    track: SambaTrackSchema,
    imageLabel: NonEmptyStringSchema,
    displayLabel: NonEmptyStringSchema,
    packages: z.array(SambaWindowsPackageReleaseSchema),
});

export const SambaLatestReleaseResponseSchema: z.ZodType<SambaLatestReleaseResponse> = z.object({
    releaseVersion: NonEmptyStringSchema,
    buildNumber: NonEmptyStringSchema,
    downloadUrl: NonEmptyStringSchema,
    sha256: NonEmptyStringSchema,
    releaseNotesUrl: NonEmptyStringSchema.optional(),
    minSupportedVersion: NonEmptyStringSchema.optional(),
    windowsTracks: z.array(SambaWindowsTrackReleaseSchema).optional(),
});

/**
 * Prunes a string to a maximum length, appending '...' if truncated
 * @param str The string to prune
 * @param maxLength Maximum length (default: 200)
 * @returns The pruned string
 */
export function pruneString(str: string, maxLength: number = 200): string {
    if (str.length <= maxLength) {
        return str;
    }
    return str.slice(0, maxLength) + '...';
}

/**
 * JSON value schema used for API payloads that store raw JSON
 */
export const JsonValueSchema: z.ZodType<JsonValue> = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(z.lazy(() => JsonValueSchema)),
    z.record(
        z.string(),
        z.lazy(() => JsonValueSchema)
    ),
]);

/**
 * Build a schema that redacts sensitive fields from a JSON record.
 * Keys listed in sensitiveFieldNames are omitted from the output.
 */
export function createRedactedRecordSchema(
    sensitiveFieldNames: ReadonlySet<string>
): z.ZodType<Record<string, JsonValue>> {
    const recordSchema: z.ZodType<Record<string, JsonValue>> = z.record(
        z.string(),
        JsonValueSchema
    );
    return recordSchema.transform((data: Record<string, JsonValue>) => {
        const redacted: Record<string, JsonValue> = {};
        for (const key in data) {
            if (!Object.prototype.hasOwnProperty.call(data, key)) {
                continue;
            }
            if (sensitiveFieldNames.has(key)) {
                continue;
            }
            const value: JsonValue | undefined = data[key];
            if (value === undefined) {
                throw new Error(`Invalid JSON record value for key "${key}"`);
            }
            redacted[key] = value;
        }
        return redacted;
    });
}

/**
 * JSON value schema that automatically prunes all strings to a maximum length
 * This is useful for logging to prevent overwhelming the logging system with large strings
 * @param maxLength Maximum length for strings (default: 200)
 * @returns A Zod schema that validates and prunes JsonValue
 */
export function createJsonValuePrunedSchema(maxStringLength: number = 200): z.ZodType<JsonValue> {
    function transform(val: unknown): JsonValue {
        const seen = new WeakSet<object>();

        function sanitize(val: unknown): JsonValue | undefined {
            // 1. Primitives & truncation (same as before)
            if (val === null) return null;
            if (val === undefined) return undefined;
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val === 'string') {
                return val.length > maxStringLength ? `${val.slice(0, maxStringLength)}...` : val;
            }
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val === 'number') return val;
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val === 'boolean') return val;
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val === 'bigint') return Number(val);
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val === 'function') return undefined;
            // eslint-disable-next-line no-restricted-syntax
            if (typeof val !== 'object') return undefined;
            if (val instanceof ZodError) {
                return {
                    name: 'ZodError',
                    issues: val.issues.map((issue) => {
                        return {
                            code: issue.code,
                            path: issue.path.join('.'),
                        };
                    }),
                };
            }

            // 2. Cycle Detection
            if (seen.has(val)) return '[Circular]';
            seen.add(val);

            // 3. Arrays
            if (Array.isArray(val)) {
                return val.map(sanitize).filter((value) => value !== undefined);
            }

            // 4. Standard Objects
            const result: Record<string, JsonValue> = {};
            for (const key in val) {
                // @ts-ignore
                const sanitizedValue = sanitize(val[key]);
                if (sanitizedValue !== undefined) {
                    result[key] = sanitizedValue;
                }
            }
            return result;
        }

        const sanitized = sanitize(val);
        if (sanitized === undefined) {
            return null;
        } else {
            return sanitized;
        }
    }

    return z.unknown().transform(transform);
}

/**
 * Default pruned JSON value schema with 200 character limit for strings
 * Use this when logging to prevent overwhelming the logging system
 */
export const JsonValuePrunedSchema: z.ZodType<JsonValue> = createJsonValuePrunedSchema(2000);

export const JsonArraySchema: z.ZodType<JsonArray> = z.array(JsonValueSchema);
export const JsonObjectSchema: z.ZodType<JsonObject> = z.record(z.string(), JsonValueSchema);

/**
 * NestJS HttpException response object structure
 * Used when HttpException.getResponse() returns an object (not a string)
 */
export const NestJsExceptionResponseSchema = z.object({
    message: z.union([z.string(), z.array(z.string())]),
    error: z.string().optional(),
    statusCode: z.number().optional(),
    errors: z
        .array(
            z.object({
                path: z.string().optional(),
                message: z.string().optional(),
            })
        )
        .optional(),
});

/**
 * CSP violation report payload
 */
export const CspViolationReportSchema: z.ZodType<unknown> = z
    .object({
        'csp-report': z
            .object({
                'document-uri': z.string().optional(),
                referrer: z.string().optional(),
                'violated-directive': z.string().optional(),
                'effective-directive': z.string().optional(),
                'original-policy': z.string().optional(),
                'blocked-uri': z.string().optional(),
                'status-code': z.number().optional(),
                'source-file': z.string().optional(),
                'line-number': z.number().optional(),
                'column-number': z.number().optional(),
            })
            .optional(),
    })
    .catchall(z.unknown());

/**
 * NestJS HttpException response structure
 * HttpException.getResponse() can return either a string or an object with message and optional error
 * This is a boundary case - third-party framework response structure
 */
// export const NestJsExceptionResponseSchema = z.union([z.string(), NestJsExceptionObjectSchema]);
